import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Menu,
  MenuList,
  MenuItem as MenuListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Autocomplete,
  Paper,
  ListItemAvatar,
  Switch
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  FlightTakeoff as FlightIcon,
  Hotel as HotelIcon,
  Event as EventIcon,
  Person as PersonIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  Groups as GroupsIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Visibility as VisibilityIcon,
  GetApp as ExportIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Calculate as CalculateIcon,
  Star as StarIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { SettingsContext } from '../../contexts/SettingsContext';
import { useReservationsData, useClientsData, usePackagesData } from '../../services/dataService';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const Reservations = () => {
  const { settings, formatCurrency, formatDate, calculateTotalWithTax } = useContext(SettingsContext);
  const navigate = useNavigate();
  
  // Get data from data services with real-time hooks
  const { reservations: reservationsData, loading: reservationsLoading } = useReservationsData();
  const { clients: clientsData, loading: clientsLoading } = useClientsData();
  const { packages: packagesData, loading: packagesLoading } = usePackagesData();
  
  const [filteredReservations, setFilteredReservations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  // Add new state for details dialog
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [reservationDetails, setReservationDetails] = useState(null);

  // Add form data state
  const [formData, setFormData] = useState({
    clientId: null,
    clientNom: '',
    clientEmail: '',
    destination: '',
    forfait: '',
    forfaitId: null,
    dateDepart: '',
    dateRetour: '',
    nombrePassagers: 1,
    prixUnitaire: 0,
    prixTotal: 0,
    acompte: 0,
    statut: 'En attente',
    statutPaiement: 'En attente',
    notes: '',
    enableTVA: true,
    customTVARate: settings.taxRate
  });

  // Function to check if data is loading
  const isLoading = reservationsLoading || clientsLoading || packagesLoading;

  // Load initial data and apply filters when data or filters change
  useEffect(() => {
    if (reservationsLoading || !reservationsData) return;
    
    let filtered = reservationsData.filter(reservation => {
      const matchesSearch = 
        reservation.clientNom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reservation.destination?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reservation.forfait?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reservation.id?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = filterStatus === 'all' || reservation.statut === filterStatus;
      
      let matchesPeriod = true;
      if (filterPeriod !== 'all') {
        const today = new Date();
        const reservationDate = new Date(reservation.dateDepart);
        
        switch (filterPeriod) {
          case 'thisMonth':
            matchesPeriod = reservationDate.getMonth() === today.getMonth() && 
                          reservationDate.getFullYear() === today.getFullYear();
            break;
          case 'nextMonth':
            const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1);
            matchesPeriod = reservationDate.getMonth() === nextMonth.getMonth() && 
                          reservationDate.getFullYear() === nextMonth.getFullYear();
            break;
          case 'upcoming':
            matchesPeriod = reservationDate >= today;
            break;
          default:
            matchesPeriod = true;
        }
      }
      
      return matchesSearch && matchesStatus && matchesPeriod;
    });
    
    setFilteredReservations(filtered);
    setPage(0);
  }, [searchTerm, filterStatus, filterPeriod, reservationsData, reservationsLoading]);

  // Smart calculation when client, package, or passengers change
  useEffect(() => {
    if (selectedPackage && formData.nombrePassagers > 0) {
      const basePrice = selectedPackage.prix * formData.nombrePassagers;
      const totalPrice = formData.enableTVA 
        ? basePrice * (1 + formData.customTVARate / 100)
        : basePrice;
      const recommendedDeposit = Math.round(totalPrice * 0.3); // 30% deposit
      
      setFormData(prev => ({
        ...prev,
        prixUnitaire: selectedPackage.prix,
        prixTotal: totalPrice,
        acompte: recommendedDeposit,
        destination: selectedPackage.destination,
        forfait: selectedPackage.titre
      }));
    }
  }, [selectedPackage, formData.nombrePassagers, formData.enableTVA, formData.customTVARate]);

  const handleOpenDialog = (reservation = null) => {
    if (reservation) {
      setEditingReservation(reservation);
      setSelectedClient(getClientById(reservation.clientId));
      setSelectedPackage(getPackageById(reservation.forfaitId));
      setFormData({
        clientId: reservation.clientId,
        clientNom: reservation.clientNom,
        clientEmail: reservation.clientEmail,
        destination: reservation.destination,
        forfait: reservation.forfait,
        forfaitId: reservation.forfaitId,
        dateDepart: reservation.dateDepart,
        dateRetour: reservation.dateRetour,
        nombrePassagers: reservation.nombrePassagers,
        prixUnitaire: reservation.prixUnitaire,
        prixTotal: reservation.prixTotal,
        acompte: reservation.acompte,
        statut: reservation.statut,
        statutPaiement: reservation.statutPaiement,
        notes: reservation.notes || '',
        enableTVA: reservation.enableTVA !== undefined ? reservation.enableTVA : true,
        customTVARate: reservation.customTVARate !== undefined ? reservation.customTVARate : settings.taxRate
      });
    } else {
      setEditingReservation(null);
      setSelectedClient(null);
      setSelectedPackage(null);
      setFormData({
        clientId: null,
        clientNom: '',
        clientEmail: '',
        destination: '',
        forfait: '',
        forfaitId: null,
        dateDepart: '',
        dateRetour: '',
        nombrePassagers: 1,
        prixUnitaire: 0,
        prixTotal: 0,
        acompte: 0,
        statut: 'En attente',
        statutPaiement: 'En attente',
        notes: '',
        enableTVA: true,
        customTVARate: settings.taxRate
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingReservation(null);
    setSelectedClient(null);
    setSelectedPackage(null);
  };

  const handleSaveReservation = () => {
    // Get the current reservations data from localStorage or use the API data
    const currentReservations = JSON.parse(localStorage.getItem('reservationsData')) || reservationsData || [];
    
    if (editingReservation) {
      // Update existing reservation
      const updatedReservations = currentReservations.map(reservation => 
        reservation.id === editingReservation.id 
          ? { ...reservation, ...formData }
          : reservation
      );
      
      // Save back to localStorage
      localStorage.setItem('reservationsData', JSON.stringify(updatedReservations));
      
      // Trigger refresh
      window.dispatchEvent(new Event('storage'));
      
      setSnackbar({
        open: true,
        message: 'Réservation modifiée avec succès',
        severity: 'success'
      });
    } else {
      // Create a new reservation
      const maxId = currentReservations.length > 0 
        ? Math.max(...currentReservations.map(r => parseInt(r.id.replace('RES', '')) || 0))
        : 0;
      
      const newReservation = {
        ...formData,
        id: `RES${String(maxId + 1).padStart(3, '0')}`,
        dateReservation: new Date().toISOString().split('T')[0]
      };
      
      const updatedReservations = [...currentReservations, newReservation];
      
      // Save back to localStorage
      localStorage.setItem('reservationsData', JSON.stringify(updatedReservations));
      
      // Trigger refresh
      window.dispatchEvent(new Event('storage'));
      
      setSnackbar({
        open: true,
        message: 'Réservation ajoutée avec succès',
        severity: 'success'
      });
    }
    handleCloseDialog();
  };

  const handleDeleteReservation = (reservationId) => {
    // Get the current reservations data from localStorage or use the API data
    const currentReservations = JSON.parse(localStorage.getItem('reservationsData')) || reservationsData || [];
    
    // Filter out the deleted reservation
    const updatedReservations = currentReservations.filter(reservation => reservation.id !== reservationId);
    
    // Save back to localStorage
    localStorage.setItem('reservationsData', JSON.stringify(updatedReservations));
    
    // Trigger refresh
    window.dispatchEvent(new Event('storage'));
    
    setSnackbar({
      open: true,
      message: 'Réservation supprimée avec succès',
      severity: 'success'
    });
    setAnchorEl(null);
  };

  const handleMenuClick = (event, reservation) => {
    setAnchorEl(event.currentTarget);
    setSelectedReservation(reservation);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedReservation(null);
  };

  const handleClientSelection = (event, newValue) => {
    setSelectedClient(newValue);
    if (newValue) {
      setFormData(prev => ({
        ...prev,
        clientId: newValue.id,
        clientNom: `${newValue.prenom} ${newValue.nom}`,
        clientEmail: newValue.email
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        clientId: null,
        clientNom: '',
        clientEmail: ''
      }));
    }
  };

  const handlePackageSelection = (event, newValue) => {
    setSelectedPackage(newValue);
    if (newValue) {
      setFormData(prev => ({
        ...prev,
        forfaitId: newValue.id,
        forfait: newValue.titre,
        destination: newValue.destination,
        prixUnitaire: newValue.prix
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        forfaitId: null,
        forfait: '',
        destination: '',
        prixUnitaire: 0
      }));
    }
  };

  const getFidelityColor = (niveau) => {
    switch(niveau) {
      case 'Premium': return 'error';
      case 'Standard': return 'info';
      case 'Bronze': return 'warning';
      default: return 'default';
    }
  };

  const getClientById = (clientId) => {
    // Normalize IDs for consistent comparison
    const normalizedClientId = clientId !== undefined && clientId !== null ? Number(clientId) : null;
    return clientsData.find(client => {
      const normalizedId = client.id !== undefined && client.id !== null ? Number(client.id) : null;
      return normalizedId === normalizedClientId;
    }) || { 
      // Return a default client object if not found
      nom: "Client supprimé", 
      prenom: "", 
      email: "",
      niveauFidelite: "Standard"
    };
  };

  const getPackageById = (packageId) => {
    return packagesData.find(pkg => pkg.id === packageId);
  };

  // Using global formatCurrency from settings context

  const getStatusColor = (statut) => {
    switch (statut) {
      case 'Confirmée': return 'success';
      case 'En attente': return 'warning';
      case 'Annulée': return 'error';
      case 'Terminée': return 'info';
      default: return 'default';
    }
  };

  const getPaymentStatusColor = (statutPaiement) => {
    switch (statutPaiement) {
      case 'Soldé': return 'success';
      case 'Acompte versé': return 'info';
      case 'En attente': return 'warning';
      case 'Annulé': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (statut) => {
    switch (statut) {
      case 'Confirmée': return <CheckCircleIcon />;
      case 'En attente': return <ScheduleIcon />;
      case 'Annulée': return <CancelIcon />;
      case 'Terminée': return <EventIcon />;
      default: return <EventIcon />;
    }
  };

  // Calculate stats for the dashboard cards
  const statsCards = React.useMemo(() => {
    if (isLoading || !reservationsData) {
      return [
        { title: 'Total Réservations', value: '-', icon: EventIcon, color: 'primary', trend: '-' },
        { title: 'Confirmées', value: '-', icon: CheckCircleIcon, color: 'success', trend: '-' },
        { title: 'En attente', value: '-', icon: ScheduleIcon, color: 'warning', trend: '-' },
        { title: 'Chiffre d\'affaires', value: '-', icon: MoneyIcon, color: 'info', trend: '-' }
      ];
    }

    return [
      {
        title: 'Total Réservations',
        value: filteredReservations.length,
        icon: EventIcon,
        color: 'primary',
        trend: '+8%'
      },
      {
        title: 'Confirmées',
        value: filteredReservations.filter(r => r.statut === 'Confirmée').length,
        icon: CheckCircleIcon,
        color: 'success',
        trend: '+12%'
      },
      {
        title: 'En attente',
        value: filteredReservations.filter(r => r.statut === 'En attente').length,
        icon: ScheduleIcon,
        color: 'warning',
        trend: '-5%'
      },
      {
        title: 'Chiffre d\'affaires',
        value: formatCurrency(filteredReservations.reduce((sum, r) => sum + (r.prixTotal || 0), 0)),
        icon: MoneyIcon,
        color: 'info',
        trend: '+18%',
        isCurrency: true
      }
    ];
  }, [filteredReservations, isLoading, reservationsData, formatCurrency]);

  // Function to format currency correctly
  const formatCurrencyForPDF = (amount) => {
    // Convert number to string with spaces as thousand separators
    const numberPart = Math.round(amount || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return `${numberPart} DZD`;
  };

  const exportReservationsToPDF = () => {
    try {
      // Create a new PDF document
      const doc = new jsPDF('landscape');
      
      // Document dimensions
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      
      // Colors
      const primaryColor = [30, 64, 175]; // blue
      const secondaryColor = [124, 58, 237]; // purple
      const successColor = [16, 185, 129]; // green
      const warningColor = [245, 158, 11]; // amber
      const errorColor = [239, 68, 68]; // red
      const textColor = [31, 41, 55]; // dark gray
      
      // Add header
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(0, 0, pageWidth, 25, 'F');
      
      // Add agency logo and name
      if (settings.agencyLogo) {
        try {
          // Add the agency logo from base64 string
          doc.addImage(settings.agencyLogo, 'PNG', margin, 5, 20, 15, undefined, 'FAST');
          
          // Add agency name
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin + 25, 15);
        } catch (logoError) {
          console.error('Error adding logo to PDF:', logoError);
          // Fallback to text only
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
        }
      } else {
        // No logo, just add agency name
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
      }
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.text("Liste des Réservations", pageWidth - margin, 17, { align: 'right' });
      
      // Add date and filters
      doc.setTextColor(textColor[0], textColor[1], textColor[2]);
      doc.setFontSize(10);
      doc.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, margin, 35);
      doc.text(`Filtres: Statut: ${filterStatus === 'all' ? 'Tous' : filterStatus} | Période: ${filterPeriod === 'all' ? 'Toutes' : filterPeriod}`, margin, 42);
      
      // Add summary section
      doc.setDrawColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
      doc.setLineWidth(0.5);
      doc.line(margin, 48, pageWidth - margin, 48);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("Résumé des Réservations", margin, 58);
      
      // Calculate some key metrics
      const totalReservations = filteredReservations.length;
      const confirmedReservations = filteredReservations.filter(r => r.statut === 'Confirmée').length;
      const pendingReservations = filteredReservations.filter(r => r.statut === 'En attente').length;
      const cancelledReservations = filteredReservations.filter(r => r.statut === 'Annulée').length;
      const totalRevenue = filteredReservations.reduce((sum, r) => sum + (r.prixTotal || 0), 0);
      const averageValue = totalReservations > 0 ? totalRevenue / totalReservations : 0;
      
      const stats = [
        { label: "Total des Réservations", value: `${totalReservations}` },
        { label: "Réservations Confirmées", value: `${confirmedReservations} (${Math.round(confirmedReservations / totalReservations * 100 || 0)}%)` },
        { label: "Réservations En Attente", value: `${pendingReservations} (${Math.round(pendingReservations / totalReservations * 100 || 0)}%)` },
        { label: "Réservations Annulées", value: `${cancelledReservations} (${Math.round(cancelledReservations / totalReservations * 100 || 0)}%)` },
        { label: "Chiffre d'Affaires", value: formatCurrencyForPDF(totalRevenue) },
        { label: "Valeur Moyenne", value: formatCurrencyForPDF(averageValue) }
      ];
      
      doc.autoTable({
        startY: 62,
        head: [['Indicateur', 'Valeur']],
        body: stats.map(item => [item.label, item.value]),
        theme: 'grid',
        headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 10 },
        margin: { left: margin, right: margin },
        tableWidth: 'auto',
        alternateRowStyles: { fillColor: [245, 247, 250] }
      });
      
      // Add destinations summary
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("Top Destinations", margin, doc.lastAutoTable.finalY + 15);
      
      // Calculate top destinations
      const destinationCounts = {};
      filteredReservations.forEach(r => {
        if (!destinationCounts[r.destination]) {
          destinationCounts[r.destination] = { count: 0, revenue: 0 };
        }
        destinationCounts[r.destination].count += 1;
        destinationCounts[r.destination].revenue += (r.prixTotal || 0);
      });
      
      const topDestinations = Object.keys(destinationCounts)
        .map(dest => ({ 
          destination: dest, 
          count: destinationCounts[dest].count,
          revenue: destinationCounts[dest].revenue
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
      
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 20,
        head: [['Destination', 'Nombre de Réservations', 'Chiffre d\'Affaires']],
        body: topDestinations.map(d => [
          d.destination, 
          d.count.toString(),
          formatCurrencyForPDF(d.revenue)
        ]),
        theme: 'grid',
        headStyles: { fillColor: secondaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 10 },
        margin: { left: margin, right: margin },
        tableWidth: 'auto',
        alternateRowStyles: { fillColor: [245, 247, 250] }
      });
      
      // Add reservation details
      doc.addPage();
      
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(0, 0, pageWidth, 25, 'F');
      
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(margin, 10, 30, 10, 1, 1, 'F');
      doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("TravelIT", margin + 6, 17);
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.text("Liste des Réservations", pageWidth - margin, 17, { align: 'right' });
      
      doc.setTextColor(textColor[0], textColor[1], textColor[2]);
      doc.setFontSize(12);
      doc.text("Détails de toutes les réservations", margin, 40);
      
      // Add reservations table
      const reservationsTableData = filteredReservations.map(r => [
        `#${r.id}`,
        r.clientNom || 'N/A',
        r.destination || 'N/A',
        `${new Date(r.dateDepart).toLocaleDateString('fr-FR')} au ${new Date(r.dateRetour).toLocaleDateString('fr-FR')}`,
        r.nombrePassagers || '0',
        formatCurrencyForPDF(r.prixTotal),
        r.statut || 'N/A',
        new Date(r.dateReservation).toLocaleDateString('fr-FR')
      ]);
      
      doc.autoTable({
        startY: 50,
        head: [['Réf.', 'Client', 'Destination', 'Dates', 'Passagers', 'Montant', 'Statut', 'Date Création']],
        body: reservationsTableData,
        theme: 'grid',
        headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 9, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 40 },
          2: { cellWidth: 40 },
          3: { cellWidth: 50 },
          4: { cellWidth: 20 },
          5: { cellWidth: 30 },
          6: { cellWidth: 25 },
          7: { cellWidth: 30 }
        },
        margin: { left: margin, right: margin },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        didDrawPage: function (data) {
          // Add header on each page
          doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
          doc.rect(0, 0, pageWidth, 25, 'F');
          
          // Add agency logo and name
          if (settings.agencyLogo) {
            try {
              // Add the agency logo from base64 string
              doc.addImage(settings.agencyLogo, 'PNG', margin, 5, 20, 15, undefined, 'FAST');
              
              // Add agency name
              doc.setTextColor(255, 255, 255);
              doc.setFontSize(12);
              doc.setFont('helvetica', 'bold');
              doc.text(settings.agencyName || 'Agence de Voyage', margin + 25, 15);
            } catch (logoError) {
              console.error('Error adding logo to PDF:', logoError);
              // Fallback to text only
              doc.setTextColor(255, 255, 255);
              doc.setFontSize(12);
              doc.setFont('helvetica', 'bold');
              doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
            }
          } else {
            // No logo, just add agency name
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
          }
          
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(16);
          doc.text("Liste des Réservations", pageWidth - margin, 17, { align: 'right' });
          
          // Add footer
          const pageNumber = doc.internal.getNumberOfPages();
          doc.setFontSize(10);
          doc.setTextColor(100, 100, 100);
          doc.text(`Page ${pageNumber}`, margin, pageHeight - 10);
          doc.text(`${settings.appName} - Système de Gestion pour Agence de Voyage`, pageWidth / 2, pageHeight - 10, { align: 'center' });
          doc.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
        }
      });
      
      // Save the PDF
      doc.save('rapport-reservations.pdf');
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Export PDF réussi',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de l\'export PDF',
        severity: 'error'
      });
    }
  };

  // Add this function to handle TVA rate changes
  const handleTVAChange = (event) => {
    const newRate = parseFloat(event.target.value);
    if (!isNaN(newRate) && newRate >= 0 && newRate <= 100) {
      setFormData(prev => {
        const basePrice = prev.prixUnitaire * prev.nombrePassagers;
        const newTotal = prev.enableTVA ? basePrice * (1 + newRate / 100) : basePrice;
        const newDeposit = Math.round(newTotal * 0.3);
        
        return {
          ...prev,
          customTVARate: newRate,
          prixTotal: newTotal,
          acompte: newDeposit
        };
      });
    }
  };
  
  // Add this function to handle enabling/disabling TVA
  const handleToggleTVA = (event) => {
    const enabled = event.target.checked;
    setFormData(prev => {
      const basePrice = prev.prixUnitaire * prev.nombrePassagers;
      const newTotal = enabled ? basePrice * (1 + prev.customTVARate / 100) : basePrice;
      const newDeposit = Math.round(newTotal * 0.3);
      
      return {
        ...prev,
        enableTVA: enabled,
        prixTotal: newTotal,
        acompte: newDeposit
      };
    });
  };

  // Add new function to handle opening the details dialog
  const handleOpenDetailsDialog = (reservation) => {
    setReservationDetails(reservation);
    setDetailsDialogOpen(true);
    handleMenuClose();
  };

  // Add new function to close the details dialog
  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setReservationDetails(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Gestion des Réservations
        </Typography>
        <Box>
          {/* Removing the Voir les Rapports button and Nouvelle Réservation button */}
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statsCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card 
              sx={{ 
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(20px)',
                borderRadius: 3,
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                }
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                      {stat.isCurrency ? formatCurrency(stat.value) : stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.title}
                    </Typography>
                    <Chip 
                      label={stat.trend} 
                      size="small" 
                      color={stat.color}
                      sx={{ mt: 1 }}
                    />
                  </Box>
                  <Avatar 
                    sx={{ 
                      bgcolor: `${stat.color}.main`,
                      width: 56,
                      height: 56
                    }}
                  >
                    <stat.icon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Search and Filters */}
      <Card 
        sx={{ 
          mb: 3,
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          borderRadius: 3,
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}
      >
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Rechercher une réservation..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Statut</InputLabel>
                <Select
                  value={filterStatus}
                  label="Statut"
                  onChange={(e) => setFilterStatus(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">Tous</MenuItem>
                  <MenuItem value="En attente">En attente</MenuItem>
                  <MenuItem value="Confirmée">Confirmées</MenuItem>
                  <MenuItem value="Terminée">Terminées</MenuItem>
                  <MenuItem value="Annulée">Annulées</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Période</InputLabel>
                <Select
                  value={filterPeriod}
                  label="Période"
                  onChange={(e) => setFilterPeriod(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">Toutes</MenuItem>
                  <MenuItem value="thisMonth">Ce mois</MenuItem>
                  <MenuItem value="nextMonth">Mois prochain</MenuItem>
                  <MenuItem value="upcoming">À venir</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<ExportIcon />}
                  onClick={exportReservationsToPDF}
                  sx={{ borderRadius: 2, flex: 1 }}
                >
                  Exporter
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                  sx={{ 
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
                    flex: 1
                  }}
                >
                  Nouvelle
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Reservations Table */}
      <Card 
        sx={{ 
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          borderRadius: 3,
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Réservation</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Voyage</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Dates</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Passagers</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Montant</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Statut</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredReservations
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((reservation) => (
                <TableRow 
                  key={reservation.id}
                  sx={{ 
                    '&:hover': { 
                      backgroundColor: 'rgba(30, 64, 175, 0.04)',
                      transform: 'scale(1.001)',
                      transition: 'all 0.2s ease'
                    }
                  }}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        #{reservation.id}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(reservation.dateReservation)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: 'primary.main',
                          width: 36,
                          height: 36
                        }}
                      >
                        {reservation.clientNom.split(' ').map(n => n[0]).join('')}
                      </Avatar>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {reservation.clientNom}
                          </Typography>
                          {reservation.clientId && getClientById(reservation.clientId) && (
                            <Chip 
                              label={getClientById(reservation.clientId).niveauFidelite}
                              size="small"
                              color={getFidelityColor(getClientById(reservation.clientId).niveauFidelite)}
                              sx={{ borderRadius: 2, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {reservation.clientEmail}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {reservation.destination}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {reservation.forfait}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatDate(reservation.dateDepart)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        au {formatDate(reservation.dateRetour)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <GroupsIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        {reservation.nombrePassagers}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {formatCurrency(reservation.prixTotal)}
                      </Typography>
                      <Chip
                        label={reservation.statutPaiement}
                        size="small"
                        color={getPaymentStatusColor(reservation.statutPaiement)}
                        sx={{ borderRadius: 2, fontSize: '0.7rem' }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        icon={getStatusIcon(reservation.statut)}
                        label={reservation.statut}
                        color={getStatusColor(reservation.statut)}
                        size="small"
                        sx={{ borderRadius: 2 }}
                      />
                      <IconButton
                        onClick={(e) => handleMenuClick(e, reservation)}
                        sx={{ 
                          transition: 'all 0.2s ease',
                          '&:hover': { 
                            backgroundColor: 'primary.light',
                            color: 'white'
                          }
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredReservations.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }
        }}
      >
        <MenuListItem onClick={() => { handleOpenDialog(selectedReservation); handleMenuClose(); }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Modifier</ListItemText>
        </MenuListItem>
        <MenuListItem onClick={() => handleOpenDetailsDialog(selectedReservation)}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Voir détails</ListItemText>
        </MenuListItem>
        <Divider />
        <MenuListItem onClick={() => { handleDeleteReservation(selectedReservation?.id); handleMenuClose(); }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Supprimer</ListItemText>
        </MenuListItem>
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {editingReservation ? 'Modifier la réservation' : 'Nouvelle réservation'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Enhanced Client Selection */}
            <Grid item xs={12}>
              <Autocomplete
                value={selectedClient}
                onChange={handleClientSelection}
                options={clientsData}
                getOptionLabel={(option) => `${option.prenom} ${option.nom} - ${option.email}`}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Sélectionner un client" 
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main', mr: 1 }}>
                        {option.prenom[0]}{option.nom[0]}
                      </Avatar>
                    </ListItemAvatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {option.prenom} {option.nom}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {option.email} • {option.adresse}
                        </Typography>
                        <Chip 
                          label={option.niveauFidelite}
                          size="small"
                          color={getFidelityColor(option.niveauFidelite)}
                          sx={{ borderRadius: 2 }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {option.preferencesVoyage.map((pref, index) => (
                          <Chip 
                            key={index}
                            label={pref}
                            size="small"
                            variant="outlined"
                            color="secondary"
                            sx={{ borderRadius: 2, fontSize: '0.7rem' }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                )}
                PaperComponent={({ children, ...props }) => (
                  <Paper 
                    {...props} 
                    sx={{ 
                      borderRadius: 2,
                      boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}
                  >
                    {children}
                  </Paper>
                )}
              />
            </Grid>

            {/* Enhanced Package Selection */}
            <Grid item xs={12}>
              <Autocomplete
                value={selectedPackage}
                onChange={handlePackageSelection}
                options={packagesData}
                getOptionLabel={(option) => `${option.titre} - ${option.destination}`}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Sélectionner un forfait" 
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <FlightIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'secondary.main', mr: 1 }}>
                        <FlightIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {option.titre}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          {formatCurrency(option.prix)}/pers
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {option.destination} • {option.duree}
                        </Typography>
                        <Chip 
                          label={option.categorie}
                          size="small"
                          color="info"
                          sx={{ borderRadius: 2 }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {option.inclusions.slice(0, 3).join(' • ')}
                        {option.inclusions.length > 3 && '...'}
                      </Typography>
                    </Box>
                  </Box>
                )}
                PaperComponent={({ children, ...props }) => (
                  <Paper 
                    {...props} 
                    sx={{ 
                      borderRadius: 2,
                      boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}
                  >
                    {children}
                  </Paper>
                )}
              />
            </Grid>
            {/* Add TVA Controls here - always visible */}
            <Grid item xs={12}>
              <Card sx={{ 
                background: 'rgba(34, 197, 94, 0.05)', 
                borderRadius: 2,
                border: '1px solid rgba(34, 197, 94, 0.2)'
              }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalculateIcon />
                    Paramètres de TVA
                  </Typography>
                  
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    bgcolor: 'rgba(255,255,255,0.7)',
                    border: '1px dashed rgba(99, 102, 241, 0.4)'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <FormControl component="fieldset">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Switch
                            checked={formData.enableTVA}
                            onChange={handleToggleTVA}
                            color="primary"
                            inputProps={{ 'aria-label': 'Activer/désactiver TVA' }}
                          />
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {formData.enableTVA ? 'TVA activée' : 'TVA désactivée'}
                          </Typography>
                        </Box>
                      </FormControl>
                      
                      {formData.enableTVA && (
                        <TextField
                          label="Taux de TVA (%)"
                          type="number"
                          value={formData.customTVARate}
                          onChange={handleTVAChange}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                          sx={{ 
                            width: '150px',
                            '& .MuiOutlinedInput-root': { borderRadius: 2 }
                          }}
                          inputProps={{ min: 0, max: 100, step: 0.5 }}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Le taux de TVA par défaut est de {settings.taxRate}%. Vous pouvez le modifier pour cette réservation.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date de départ"
                type="date"
                value={formData.dateDepart}
                onChange={(e) => setFormData({...formData, dateDepart: e.target.value})}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date de retour"
                type="date"
                value={formData.dateRetour}
                onChange={(e) => setFormData({...formData, dateRetour: e.target.value})}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Nombre de passagers"
                type="number"
                value={formData.nombrePassagers}
                onChange={(e) => setFormData({...formData, nombrePassagers: parseInt(e.target.value)})}
                inputProps={{ min: 1 }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            {/* Smart Pricing Calculator - keep the existing code but remove the TVA controls from here */}
            {selectedPackage && formData.nombrePassagers > 0 && (
              <Grid item xs={12}>
                <Card sx={{ 
                  background: 'rgba(34, 197, 94, 0.05)', 
                  borderRadius: 2,
                  border: '1px solid rgba(34, 197, 94, 0.2)'
                }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalculateIcon />
                      Calcul automatique des prix
                    </Typography>
                    
                    {/* Remove TVA Controls from here, as they are now in a separate section */}
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, background: 'rgba(255,255,255,0.8)', borderRadius: 2 }}>
                          <Typography variant="body2" color="text.secondary">Prix unitaire</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            {formatCurrency(formData.prixUnitaire)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, background: 'rgba(255,255,255,0.8)', borderRadius: 2 }}>
                          <Typography variant="body2" color="text.secondary">Sous-total</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: 'info.main' }}>
                            {formatCurrency(formData.prixUnitaire * formData.nombrePassagers)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, background: 'rgba(255,255,255,0.8)', borderRadius: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            {formData.enableTVA ? `Taxes (${formData.customTVARate}%)` : 'Taxes (0%)'}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: 'warning.main' }}>
                            {formatCurrency(formData.enableTVA 
                              ? formData.prixTotal - (formData.prixUnitaire * formData.nombrePassagers) 
                              : 0
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, background: 'rgba(255,255,255,0.8)', borderRadius: 2 }}>
                          <Typography variant="body2" color="text.secondary">Total TTC</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                            {formatCurrency(formData.prixTotal)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={`Acompte recommandé (${formData.prixTotal ? Math.round((formData.acompte / formData.prixTotal) * 100) : 30}%)`}
                type="number"
                value={formData.acompte}
                onChange={(e) => setFormData({...formData, acompte: parseFloat(e.target.value)})}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PaymentIcon sx={{ color: 'success.main' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                helperText={formData.acompte > 0 ? `Restant à payer: ${formatCurrency(formData.prixTotal - formData.acompte)}` : ''}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                p: 2, 
                background: 'rgba(124, 58, 237, 0.05)', 
                borderRadius: 2,
                border: '1px solid rgba(124, 58, 237, 0.2)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Récapitulatif financier
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">Total voyage:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatCurrency(formData.prixTotal)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">Acompte:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                    {formatCurrency(formData.acompte)}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Solde restant:</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'error.main' }}>
                    {formatCurrency(formData.prixTotal - formData.acompte)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Statut de la réservation</InputLabel>
                <Select
                  value={formData.statut}
                  label="Statut de la réservation"
                  onChange={(e) => setFormData({...formData, statut: e.target.value})}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="En attente">En attente</MenuItem>
                  <MenuItem value="Confirmée">Confirmée</MenuItem>
                  <MenuItem value="Terminée">Terminée</MenuItem>
                  <MenuItem value="Annulée">Annulée</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Statut du paiement</InputLabel>
                <Select
                  value={formData.statutPaiement}
                  label="Statut du paiement"
                  onChange={(e) => setFormData({...formData, statutPaiement: e.target.value})}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="En attente">En attente</MenuItem>
                  <MenuItem value="Acompte versé">Acompte versé</MenuItem>
                  <MenuItem value="Soldé">Soldé</MenuItem>
                  <MenuItem value="Annulé">Annulé</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleCloseDialog} sx={{ borderRadius: 2 }}>
            Annuler
          </Button>
          <Button 
            onClick={handleSaveReservation}
            variant="contained"
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)'
            }}
          >
            {editingReservation ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetailsDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Détails de la Réservation
            </Typography>
            <Chip
              label={reservationDetails?.statut || 'N/A'}
              color={getStatusColor(reservationDetails?.statut)}
              icon={getStatusIcon(reservationDetails?.statut)}
              sx={{ borderRadius: 2, fontWeight: 600 }}
            />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {reservationDetails && (
            <Grid container spacing={3}>
              {/* Header Info */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'primary.light', color: 'white' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      #{reservationDetails.id}
                    </Typography>
                    <Typography variant="subtitle1">
                      Créée le {formatDate(reservationDetails.dateReservation)}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              
              {/* Client Info */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%', borderRadius: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                        {reservationDetails.clientNom?.split(' ').map(n => n[0]).join('')}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {reservationDetails.clientNom}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {reservationDetails.clientEmail}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    {getClientById(reservationDetails.clientId) && (
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Téléphone
                          </Typography>
                          <Typography variant="body2">
                            {getClientById(reservationDetails.clientId).telephone}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Niveau de fidélité
                          </Typography>
                          <Chip 
                            label={getClientById(reservationDetails.clientId).niveauFidelite}
                            size="small"
                            color={getFidelityColor(getClientById(reservationDetails.clientId).niveauFidelite)}
                            sx={{ borderRadius: 2 }}
                          />
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Adresse
                          </Typography>
                          <Typography variant="body2" align="right">
                            {getClientById(reservationDetails.clientId).adresse}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Voyage Info */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%', borderRadius: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'secondary.main', width: 48, height: 48 }}>
                        <FlightIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {reservationDetails.destination}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {reservationDetails.forfait}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Dates de voyage
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(reservationDetails.dateDepart)} au {formatDate(reservationDetails.dateRetour)}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Nombre de passagers
                      </Typography>
                      <Typography variant="body2">
                        {reservationDetails.nombrePassagers}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Prix unitaire
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(reservationDetails.prixUnitaire)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Financial Info */}
              <Grid item xs={12}>
                <Card sx={{ borderRadius: 2, bgcolor: 'rgba(245, 247, 250, 0.8)' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Détails financiers
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 2 }}>
                          <Typography variant="body2" color="text.secondary">Sous-total</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {formatCurrency(reservationDetails.prixUnitaire * reservationDetails.nombrePassagers)}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            {reservationDetails.enableTVA !== undefined ? 
                              (reservationDetails.enableTVA ? `TVA (${reservationDetails.customTVARate || settings.taxRate}%)` : 'TVA (0%)') : 
                              `TVA (${settings.taxRate}%)`
                            }
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {formatCurrency(
                              reservationDetails.prixTotal - (reservationDetails.prixUnitaire * reservationDetails.nombrePassagers)
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 2 }}>
                          <Typography variant="body2" color="text.secondary">Total TTC</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {formatCurrency(reservationDetails.prixTotal)}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} md={3}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 2 }}>
                          <Typography variant="body2" color="text.secondary">Statut de paiement</Typography>
                          <Chip 
                            label={reservationDetails.statutPaiement}
                            color={getPaymentStatusColor(reservationDetails.statutPaiement)}
                            sx={{ mt: 1, borderRadius: 2 }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                    
                    <Box sx={{ mt: 3, p: 2, bgcolor: 'white', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>Acompte versé</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>
                          {formatCurrency(reservationDetails.acompte)}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>Solde restant</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'error.main' }}>
                          {formatCurrency(reservationDetails.prixTotal - reservationDetails.acompte)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Notes */}
              {reservationDetails.notes && (
                <Grid item xs={12}>
                  <Card sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Notes
                      </Typography>
                      <Typography variant="body1">
                        {reservationDetails.notes}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDetailsDialog} variant="outlined" sx={{ borderRadius: 2 }}>
            Fermer
          </Button>
          <Button 
            onClick={() => { handleOpenDialog(reservationDetails); handleCloseDetailsDialog(); }}
            variant="contained" 
            startIcon={<EditIcon />}
            sx={{ borderRadius: 2 }}
          >
            Modifier
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({...snackbar, open: false})}
      >
        <Alert 
          onClose={() => setSnackbar({...snackbar, open: false})} 
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Reservations; 